import { useMemo, useState } from 'react';

import { MutationResult } from '@apollo/client';
import { Spinner } from '@blueprintjs/core';
import { Button, InputAdornment, TextField } from '@opendoor/mosaic';

import { WebNoteFields } from '../../graphql/athena/types/WebNoteFields';
import { urlify } from '../../utils/string';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';
import { DateText } from '../text/DateText';

interface MessageThreadProps {
  messages: WebNoteFields[];
  allowEdit?: boolean;

  // option prop for a title that is shown above
  // the textbox and mesasge thread
  title?: string;

  // if allowEdit, optional prop to define what the
  // placeholder text in the textbox is. Defaults to
  // "Type message here"
  textboxPlaceholderText?: string;

  // if allowEdit, optional prop to handle submission of new message
  onSubmitMessage?: (message: string) => void;

  // if onSubmitMessage is a mutation, this is the mutation's response
  // which will contain loading and error values
  submitMessageResponse?: MutationResult<any>;
}

const COLORS = [
  'red',
  'green',
  'blue',
  'purple',
  'chocolate',
  'navy',
  'brown',
  '#008B8B', //dark cyan
  '#8A2BE2', //blue violet
  '#FF1493', //deep pink
  '#FF4500', //orange red
];

const MessageThread = ({
  messages,
  allowEdit = false,
  title,
  textboxPlaceholderText = 'Type message here',
  onSubmitMessage,
  submitMessageResponse,
}: MessageThreadProps) => {
  const [newMessage, setNewMessage] = useState('');
  const orderedMessages = useMemo(
    () =>
      messages
        .map((m) => m)
        .sort(
          (a: WebNoteFields, b: WebNoteFields) =>
            (b.createdAt ? new Date(b.createdAt) : new Date()).getTime() -
            (a.createdAt ? new Date(a.createdAt) : new Date()).getTime(),
        ),
    [messages],
  );
  const colorMapping = useMemo(() => {
    let j = 0;
    return orderedMessages?.reduce((map: any, message: WebNoteFields) => {
      const createdBy = message.user?.fullName;
      if (createdBy && !map[createdBy]) map[createdBy] = COLORS[j++ % COLORS.length];
      return map;
    }, {});
  }, [orderedMessages]);
  const submitMessageLoading = submitMessageResponse?.loading || false;

  const submitButton = (
    <InputAdornment position="end">
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          if (onSubmitMessage) {
            onSubmitMessage(newMessage);
          }
          setNewMessage('');
        }}
        disabled={!newMessage || submitMessageLoading}
      >
        Post
      </Button>
    </InputAdornment>
  );

  return (
    <VerticalLayout>
      {title ? <span style={styles.title}>{title}</span> : null}
      {allowEdit ? (
        <TextField
          data-testid="maintenance-message-text-field"
          placeholder={textboxPlaceholderText}
          rows={4}
          rowsMax={5}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          multiline
          fullWidth
          className="mt-5"
          InputProps={{
            endAdornment: <>{submitMessageLoading ? <Spinner /> : submitButton}</>,
          }}
        />
      ) : null}
      {orderedMessages.map((message, i) => (
        <div key={`message-${i}`}>
          <VerticalLayout className="mt-5">
            <HorizontalLayout>
              {message.user?.fullName && (
                <span style={{ color: colorMapping[message.user.fullName], fontWeight: 'bold' }}>
                  {message.user.fullName}
                </span>
              )}
              {message.createdAt && (
                <div className="ml-3">
                  <DateText date={new Date(message.createdAt)} format="short-with-time" />
                </div>
              )}
            </HorizontalLayout>
            <span>{urlify(message.body || '')}</span>
          </VerticalLayout>
        </div>
      ))}
    </VerticalLayout>
  );
};

export default MessageThread;

const styles = {
  title: {
    fontWeight: '600',
    fontSize: 16,
    color: 'black',
  },
};
